import type { HTMLAttributeAnchorTarget } from 'react';
import type { To } from 'react-router';
import React, { useCallback, useId } from 'react';
import { useNavigate } from 'react-router';

import type { IconName } from '../../assets/Icon/Icon';
import type {
  PolymorphicComponentProps,
  PolymorphicRef,
} from '../../utilities/types/polymorphicAsProp';
import type { BadgeProps } from '../Badge/Badge';
import { Divider } from '../../assets/Divider/Divider';
import { Icon } from '../../assets/Icon/Icon';
import { selectors, transitions } from '../../controls/shared/styles';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { isDefined } from '../../utilities/isDefined';
import { HStack } from '../../utilities/Stack/HStack';
import {
  type AccessLevelProp,
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import { useViewport } from '../../utilities/useViewport';
import { Badge } from '../Badge/Badge';
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
} from '../DropdownMenu/DropdownMenu';
import { IconTooltip } from '../Tooltip/Tooltip';

const TabIcon = styled(Icon, {
  color: '$$tabIconColor',

  variants: {
    hasSize: {
      'x-small': {
        width: '$12',
        height: '$12',
      },

      small: {
        width: '$14',
        height: '$14',
      },
    },
  },
});
TabIcon.displayName = 'TabIcon';

const TabLabel = styled(Text, {
  color: '$$tabLabelColor',
  fontWeight: '$bold',
});
TabLabel.displayName = 'TabLabel';

const TabContainer = styled('button', {
  position: 'relative',
  display: 'flex',
  boxShadow: '$$focusRing',
  whiteSpace: 'nowrap',
  transition: transitions.control,

  [selectors.hover]: {
    backgroundColor: colors.bgNeutralLight,
    $$tabLabelColor: colors.headingNeutralLight,
    $$tabIconColor: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgNeutralDark,
      $$tabLabelColor: colors.headingNeutralDark,
      $$tabIconColor: colors.bodyNeutralDark,
    },
  },

  [selectors.focus]: {
    outline: 'none',
    $$focusRing: shadows.focusRingLight,

    [darkThemeSelector]: {
      $$focusRing: shadows.focusRingDark,
    },
  },

  variants: {
    hasAnnotation: {
      true: {
        '@notDesktop': {
          paddingRight: '$6',
        },

        '@desktop': {
          paddingRight: '$4',
        },
      },
    },
    hasSubtabs: {
      true: {},
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    disabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
    isSelected: {
      true: {
        [selectors.all]: {
          backgroundColor: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,
          $$tabLabelColor: colors.headingBrandLight,
          $$tabIconColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
            $$tabLabelColor: colors.headingBrandDark,
            $$tabIconColor: colors.bodyBrandDark,
          },
        },
      },
      false: {
        $$tabLabelColor: colors.headingNeutralLight,
        $$tabIconColor: colors.bodyNeutralLight,

        [darkThemeSelector]: {
          $$tabLabelColor: colors.headingNeutralDark,
          $$tabIconColor: colors.bodyNeutralDark,
        },

        [selectors.hover]: {
          $$tabLabelColor: colors.headingNeutralLight,
          $$tabIconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            $$tabLabelColor: colors.headingNeutralDark,
            $$tabIconColor: colors.bodyNeutralDark,
          },
        },
      },
    },
    hasSize: {
      'x-small': {
        '@notDesktop': {
          padding: '$2 $6',
          borderRadius: '$8',
        },

        '@desktop': {
          padding: '0 $4',
          borderRadius: '$6',
        },
      },
      small: {
        padding: '$4 $8',
        borderRadius: '$10',
      },
    },
  },
  compoundVariants: [
    {
      hasSubtabs: false,
      isSelected: true,
      css: {},
    },
    {
      internal: true,
      css: {
        $$tabLabelColor: colors.internalBodyLight,
        $$tabIconColor: colors.internalIconLight,

        [selectors.hover]: {
          $$tabLabelColor: colors.internalHeadingLight,
          $$tabIconColor: colors.internalBodyLight,
        },

        [darkThemeSelector]: {
          $$tabLabelColor: colors.internalBodyDark,
          $$tabIconColor: colors.internalIconDark,

          [selectors.hover]: {
            $$tabLabelColor: colors.internalHeadingDark,
            $$tabIconColor: colors.internalBodyDark,
          },
        },
      },
    },
    {
      internal: true,
      isSelected: true,
      css: {
        [selectors.all]: {
          backgroundColor: colors.internalBgLight,
          strokeAll: colors.internalStrokeLight,
          $$tabLabelColor: colors.internalHeadingLight,
          $$tabIconColor: colors.internalBodyLight,

          [darkThemeSelector]: {
            backgroundColor: colors.internalBgDark,
            strokeAll: colors.internalStrokeDark,
            $$tabLabelColor: colors.internalHeadingDark,
            $$tabIconColor: colors.internalBodyDark,
          },
        },
      },
    },
  ],
});
TabContainer.displayName = 'TabContainer';

type TabPropSize = 'x-small' | 'small';
export interface TabProps {
  /**
   * The level required to access the tab.
   */
  accessLevel?: AccessLevel | null;
  /**
   * Additional annotation such a result count within the tab.
   * */
  annotation?: React.ReactNode | BadgeProps;
  /**
   * Written label for the tab.
   * */
  children?: React.ReactNode;
  /**
   * Set which icon to display, no value displays no icon.
   */
  icon?: IconName;
  /**
   * Boolean to show internal-only styles.
   */
  internal?: boolean;
  /**
   * Key for the tab.
   */
  key?: string;
  /**
   * Written label for the tab.
   */
  label?: React.ReactNode;
  /**
   * Handles the onClick event.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * Marks the currently selected tab.
   */
  selected?: boolean;
  /**
   * Whether the tab is disabled.
   */
  disabled?: boolean;
  /**
   * Size of the tab.
   */
  size?: TabPropSize;
  /**
   * Whether the tab has subtabs.
   */
  subtabs?: TabProps[];
  /**
   * A tooltip to be displayed on hover.
   */
  tooltip?: React.ReactNode;
  /**
   * Routing
   */
  as?: React.ElementType;
  to?: To;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
}

function filterOutNullAccessLevels(tab: TabProps): tab is typeof tab & {
  accessLevel: NonNullable<(typeof tab)['accessLevel']> | undefined;
} {
  return tab.accessLevel !== null;
}

function hasAnnotationContent(annotation: TabProps['annotation']): boolean {
  if (!annotation) return false;
  if (typeof annotation === 'string' || typeof annotation === 'number') {
    return true;
  }
  if (typeof annotation === 'object' && 'label' in annotation) {
    return true;
  }
  return false;
}

function TabAnnotation({
  accessLevel: accessLevelProp,
  annotation,
  internal,
  size,
  selected,
}: {
  accessLevel?: AccessLevelProp | null;
  annotation: TabProps['annotation'];
  internal: boolean;
  size: TabPropSize;
  selected: boolean;
}) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const isInternal = internal || accessLevel === AccessLevel.Internal;

  if (!annotation) return null;

  if (typeof annotation === 'string' || typeof annotation === 'number') {
    return (
      <Badge internal={isInternal} size={size} variant={selected ? 'brand' : 'neutral'}>
        {annotation}
      </Badge>
    );
  }
  if (typeof annotation === 'object' && 'label' in annotation) {
    const annotationVariant = annotation.variant || 'neutral';
    return (
      <Badge
        {...annotation}
        arrangement={annotation.arrangement ?? 'leading-icon'}
        internal={isInternal}
        size={size}
        variant={!annotation.variant && selected ? 'brand' : annotationVariant}
      >
        {annotation.label}
      </Badge>
    );
  }

  return null;
}

function TabInner<Tag extends React.ElementType>(
  {
    accessLevel: accessLevelProp,
    annotation,
    children,
    icon,
    internal,
    label,
    selected = false,
    disabled = false,
    size = 'small',
    subtabs,
    tooltip,
    onClick,
    as,
    to,
    href,
    target,
    ...remaining
  }: PolymorphicComponentProps<Tag, TabProps>,
  forwardedRef: PolymorphicRef<Tag>,
) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const isInternal = internal || accessLevel === AccessLevel.Internal;
  if (accessLevel === null) return null;

  const hasEnd =
    hasAnnotationContent(annotation) ||
    (accessLevel && isAccessLevelVisible(accessLevel)) ||
    isDefined(tooltip);

  return (
    <TabContainer
      type="button"
      role="tab"
      aria-selected={selected ? 'true' : 'false'}
      ref={forwardedRef}
      isSelected={selected}
      hasAnnotation={hasAnnotationContent(annotation)}
      hasSubtabs={isDefined(subtabs)}
      internal={isInternal}
      hasSize={size}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      as={disabled ? undefined : as}
      to={disabled ? undefined : to}
      href={disabled ? undefined : href}
      target={disabled ? undefined : target}
      {...remaining}
    >
      <AlignStack
        direction="row"
        gap={size === 'x-small' ? 4 : 6}
        preset={size === 'x-small' ? 'small' : 'body'}
        start={isDefined(icon) && <TabIcon hasSize={size} icon={icon} />}
        end={
          hasEnd && (
            <HStack align="center" spacing={4}>
              {isDefined(annotation) && (
                <TabAnnotation
                  accessLevel={accessLevel}
                  annotation={annotation}
                  internal={isInternal}
                  size={size}
                  selected={selected}
                />
              )}
              {accessLevel && isAccessLevelVisible(accessLevel) && (
                <FeatureBadge type={accessLevel} size="x-small" />
              )}
              {tooltip && (
                <IconTooltip
                  icon="question"
                  size={14}
                  contents={tooltip}
                  variant={selected ? 'brand' : 'neutral'}
                />
              )}
            </HStack>
          )
        }
      >
        <TabLabel>{label || children}</TabLabel>
      </AlignStack>
    </TabContainer>
  );
}

export const Tab = React.forwardRef(TabInner) as <D extends React.ElementType = 'button'>(
  props: PolymorphicComponentProps<D, TabProps> & { ref?: PolymorphicRef<D> },
) => ReturnType<typeof TabInner>;

const TabDivider = styled(Divider, {
  padding: '$4',
});
TabDivider.displayName = 'TabDivider';

const TabsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$4',
  alignItems: 'center',
  height: '100%',

  '@notDesktop': {
    width: '100%',
  },
});
TabsContainer.displayName = 'TabsContainer';

export type TabsProps = {
  /**
   * Pass `Tab` components into this for automatically laid out tabs.
   * */
  children?: React.ReactNode;
  /**
   * Size of the tabs.
   */
  size?: TabPropSize;
  /**
   * Tabs passed in as an array.
   */
  tabs?: TabProps[];
  /**
   * Secondary tabs passed in as an array.
   */
  secondaryTabs?: TabProps[];
  /**
   * If only one tab is visible, don't display it.
   */
  hideSingleTab?: boolean;
};

function TabsArray({ size, tabs, secondaryTabs, hideSingleTab = true }: TabsProps) {
  const { breakpoint } = useViewport();
  const navigate = useNavigate();
  const key = useId();
  const selectedTab =
    tabs?.find((tab) => tab.selected) || secondaryTabs?.find((tab) => tab.selected);
  const selectedSubtab = selectedTab?.subtabs?.find((subtab) => subtab.selected);

  const onClickTab = useCallback(
    (tab: TabProps, e: Event) => {
      if (tab.to) {
        navigate(tab.to);
      }

      tab.onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
    },
    [navigate],
  );

  const filteredTabs = tabs?.filter(filterOutNullAccessLevels);
  const filteredSecondaryTabs = secondaryTabs?.filter(filterOutNullAccessLevels);

  // Don't render needless tabs if there's only one option
  if (hideSingleTab && filteredTabs?.length === 1 && !filteredSecondaryTabs?.length) return null;

  return breakpoint === 'desktop' ? (
    <>
      {filteredTabs?.map((tab) => (
        <React.Fragment key={`${key}-tab-${tab.label}`}>
          <Tab size={size} {...tab} />
          {tab.selected && tab.subtabs?.length && (
            <>
              <Icon icon="chevron-right" size={12} variant="brand" />
              {tab.subtabs?.map((subtab) => (
                <Tab size={size} key={`${key}-subtab-${subtab.label}`} {...subtab} />
              ))}
            </>
          )}
        </React.Fragment>
      ))}
      {!!filteredTabs?.length && !!filteredSecondaryTabs?.length && (
        <TabDivider appearance="thick" direction="column" variant="neutral" />
      )}
      {filteredSecondaryTabs?.map((secondaryTab) => (
        <React.Fragment key={`${key}-secondaryTab-${secondaryTab.label}`}>
          <Tab size={size} {...secondaryTab} />
          {secondaryTab.selected && secondaryTab.subtabs?.length && (
            <>
              <Icon icon="chevron-right" size={12} variant="brand" />
              {secondaryTab.subtabs?.map((subtab) => (
                <Tab size={size} key={`${key}-subtab-${subtab.label}`} {...subtab} />
              ))}
            </>
          )}
        </React.Fragment>
      ))}
    </>
  ) : (
    <DropdownMenu>
      <DropdownMenuButton
        arrangement="leading-icon"
        icon={selectedTab?.icon}
        labelAlign="start"
        labelStretch
        size="medium"
        width="100%"
        annotation={
          typeof selectedTab?.annotation !== 'object' ? selectedTab?.annotation : undefined
        }
      >
        {selectedTab?.label} {selectedSubtab ? ` > ${selectedSubtab.label}` : null}
      </DropdownMenuButton>
      <DropdownMenuPopover align="start" matchTrigger>
        {!!filteredTabs?.length && (
          <DropdownMenuGroup>
            {filteredTabs.map((tab) => {
              const hasSubtabs = tab.subtabs?.length;
              if (hasSubtabs) {
                return (
                  <DropdownMenuGroup key={`${key}-tab-${tab.label}`} label={tab.label} isNested>
                    {tab.subtabs?.map((subtab) => (
                      <DropdownMenuItem
                        key={`${key}-subtab-${subtab.label}`}
                        onSelect={(e) => onClickTab(subtab, e)}
                        accessLevel={subtab.accessLevel ?? undefined}
                        annotation={
                          typeof subtab.annotation !== 'object' ? subtab.annotation : undefined
                        }
                        icon={subtab.icon}
                        label={subtab.label || subtab.children}
                        internal={tab.internal}
                        tooltip={tab.tooltip}
                        selected={selectedSubtab?.label === subtab.label}
                        disabled={subtab.disabled}
                      />
                    ))}
                  </DropdownMenuGroup>
                );
              }

              return (
                <DropdownMenuItem
                  key={`${key}-tab-${tab.label}`}
                  onSelect={(e) => onClickTab(tab, e)}
                  accessLevel={tab.accessLevel}
                  annotation={typeof tab.annotation !== 'object' ? tab.annotation : undefined}
                  icon={tab.icon}
                  label={tab.label || tab.children}
                  internal={tab.internal}
                  tooltip={tab.tooltip}
                  selected={selectedTab?.label === tab.label}
                  disabled={tab.disabled}
                />
              );
            })}
          </DropdownMenuGroup>
        )}
        {!!filteredSecondaryTabs?.length && (
          <DropdownMenuGroup>
            {filteredSecondaryTabs.map((secondaryTab) => (
              <DropdownMenuItem
                key={`${key}-secondaryTab-${secondaryTab.label}`}
                onSelect={(e) => onClickTab(secondaryTab, e)}
                accessLevel={secondaryTab.accessLevel}
                annotation={
                  typeof secondaryTab.annotation !== 'object' ? secondaryTab.annotation : undefined
                }
                icon={secondaryTab.icon}
                label={secondaryTab.label || secondaryTab.children}
                internal={secondaryTab.internal}
                tooltip={secondaryTab.tooltip}
                selected={selectedTab?.label === secondaryTab.label}
                disabled={secondaryTab.disabled}
              />
            ))}
          </DropdownMenuGroup>
        )}
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

export function Tabs({
  children,
  size,
  tabs,
  secondaryTabs,
  hideSingleTab,
  ...remaining
}: TabsProps) {
  const isArray = Array.isArray(tabs) || Array.isArray(secondaryTabs);
  return (
    <TabsContainer role="tablist" {...remaining}>
      {isArray ? (
        <TabsArray
          size={size}
          tabs={tabs}
          secondaryTabs={secondaryTabs}
          hideSingleTab={hideSingleTab}
        />
      ) : (
        children || tabs || secondaryTabs
      )}
    </TabsContainer>
  );
}
